import {
  createStyles,
  ListSubheader,
  MenuItem,
  MenuList,
  Typography,
  withStyles,
  type Theme,
  type WithStyles,
} from "@material-ui/core";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageIcon from "@mui/icons-material/Language";
import { Locales, useTranslations } from "adapters/i18n";
import React, { RefObject } from "react";
import { MenuButton, MenuButtonTrigger, useMenuButton } from "../MenuButton";

const ID = "change-language-select";

export type LanguageSelectProps = WithStyles<typeof styles>;

const LanguageSelect = ({ classes }: LanguageSelectProps) => {
  const { currentLanguage, handleChangeLanguage, supportedLanguages, t } =
    useTranslations();

  const {
    handleClickAway,
    handleCloseMenu,
    handleToggleMenu,
    isMenuOpen,
    triggerRef,
  } = useMenuButton();

  const handleSelectLanguage = (language: Locales) => {
    handleChangeLanguage(language);
    handleCloseMenu();
  };

  return (
    <MenuButton
      classNames={{ paper: classes.paper }}
      id={ID}
      isOpen={isMenuOpen}
      onClickAway={handleClickAway}
      placement="bottom-end"
      trigger={
        <SelectButton
          classes={classes}
          isMenuOpen={isMenuOpen}
          onClick={handleToggleMenu}
          triggerRef={triggerRef}
        />
      }
      triggerRef={triggerRef}
    >
      <MenuList
        aria-setsize={supportedLanguages.length}
        className={classes.menuList}
      >
        <ListSubheader className={classes.listSubheader}>
          <LanguageIcon className={classes.languageIconForList} />
          <Typography className={classes.subheaderTitle}>
            {`${t("common:language")}:`}
          </Typography>
        </ListSubheader>

        {supportedLanguages.map((lang, index) => (
          <LanguageOptionItem
            key={lang.locale}
            classes={classes}
            index={index}
            isSelected={lang.locale === currentLanguage.locale}
            label={lang.name}
            locale={lang.locale}
            onSelectOption={handleSelectLanguage}
          />
        ))}
      </MenuList>
    </MenuButton>
  );
};

type SelectButtonProps = {
  isMenuOpen: boolean;
  onClick: () => void;
  triggerRef: RefObject<HTMLButtonElement>;
} & WithStyles<typeof styles>;

const SelectButton = ({
  classes,
  isMenuOpen,
  onClick,
  triggerRef,
}: SelectButtonProps) => {
  const { currentLanguage, t } = useTranslations();

  const selectedLanguage = currentLanguage.locale.split("-")[0];

  return (
    <MenuButtonTrigger
      aria-label={t("common:change-language")}
      className={classes.button}
      isListOpen={isMenuOpen}
      listId={ID}
      onClick={onClick}
      ref={triggerRef}
    >
      <div className={classes.buttonContentWrapper}>
        <LanguageIcon className={classes.languageIconForButton} />

        <Typography id="current-language" className={classes.buttonText}>
          {selectedLanguage}
        </Typography>

        <ExpandMoreIcon className={classes.expandIcon} fontSize="inherit" />
      </div>
    </MenuButtonTrigger>
  );
};

type LanguageOptionItemProps = {
  index: number;
  isSelected: boolean;
  onSelectOption: (locale: Locales) => void;
  label: string;
  locale: Locales;
} & WithStyles<typeof styles>;

const LanguageOptionItem = ({
  classes,
  index,
  isSelected,
  onSelectOption,
  label,
  locale,
}: LanguageOptionItemProps) => {
  const handleSelectItem = () => {
    onSelectOption(locale);
  };

  const rootClassName = isSelected
    ? classes.selectedListOption
    : classes.listOption;

  return (
    <MenuItem
      aria-posinset={index}
      aria-selected={isSelected}
      className={rootClassName}
      onClick={handleSelectItem}
    >
      {label}
      {isSelected && <CheckCircleIcon className={classes.selectedItemIcon} />}
    </MenuItem>
  );
};

const styles = (theme: Theme) => {
  const defaultListOption = {
    alignItems: "center",
    borderRadius: "8px",
    flexDirection: "row",
    fontSize: "16px",
    display: "flex",
    gap: "24px",
    justifyContent: "space-between",
    minWidth: "234px",
    padding: "8px 16px",
  } as const;

  return createStyles({
    button: {
      height: "100%",
    },
    buttonContentWrapper: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      gap: "8px",
    },
    languageIconForButton: {
      color: theme.palette.primary.main,
      fontSize: "16px !important",
    },
    languageIconForList: {
      color: theme.palette.primary.main,
      fontSize: "20px !important",
    },
    buttonText: {
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: "24px",
      color: "#333333",
      textTransform: "uppercase",
    },
    expandIcon: {
      color: "#616161",
      fontSize: "24px !important",
    },
    listSubheader: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      gap: "8px",
      padding: 0,
      marginBottom: "16px",
    },
    subheaderTitle: {
      fontSize: "16px",
      fontWeight: 700,
      letterSpacing: 0,
      lineHeight: "100%",
      color: "#333333",
    },
    menuList: {
      flexDirection: "column",
      display: "flex",
      gap: "8px",
      padding: 0,
    },
    listOption: {
      ...defaultListOption,
      backgroundColor: theme.palette.common.white,
      color: "#616161",
      fontWeight: 400,
    },
    selectedListOption: {
      ...defaultListOption,
      backgroundColor: "#F0F6FA",
      color: "#333333",
      fontWeight: 500,
    },
    selectedItemIcon: {
      color: theme.palette.primary.main,
      fontSize: "20px !important",
    },
    paper: {
      padding: "24px 16px",
    },
  });
};

export default withStyles(styles)(LanguageSelect);
